<script setup>
/* global axios */
import { h, ref, computed, onMounted, watch } from 'vue';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRoute } from 'vue-router';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import { useSidebar } from 'dashboard/composables/useSidebar';
import { useSidebarStyle } from 'dashboard/composables/useSidebarStyle';
import { useButtonStyle } from 'dashboard/composables/useButtonStyle';
import { useBrandedAccount } from 'dashboard/composables/useBrandedAccount';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAgentName } from 'dashboard/composables/useAgentName';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import SidebarAnnouncementBell from './SidebarAnnouncementBell.vue';
import SidebarSupportBell from './SidebarSupportBell.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();
const { isStacklab } = useConfig();
const { agentName } = useAgentName();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);
const hasBillingPlan = ref(false);

const { isCollapsed, toggleSidebar } = useSidebar();
const { isCustomStyleActive, sidebarStyleVars } = useSidebarStyle();
useButtonStyle();
useBrandedAccount();

const toggleSidebarTooltip = computed(() =>
  isCollapsed.value
    ? t('SIDEBAR.EXPAND_SIDEBAR')
    : t('SIDEBAR.COLLAPSE_SIDEBAR')
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const dashboardApps = useMapGetter('dashboardApps/getNavigationApps');
const funnels = useMapGetter('funnel/getFunnels');
const kanbanItems = useMapGetter('kanban/getKanbanItems');
const selectedFunnel = useMapGetter('funnel/getSelectedFunnel');

onMounted(async () => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  store.dispatch('dashboardApps/get');
  store.dispatch('platformTooltips/fetch');
  store.dispatch('platformAnnouncements/fetch');
  store.dispatch('changelogEntries/fetchUnreadCount');
  store.dispatch('funnel/fetch');
  store.dispatch('kanban/fetchKanbanConfig');

  // Lightweight check: does this account have an active billing plan?
  try {
    const accountId = store.getters.getCurrentAccountId;
    if (accountId) {
      const res = await axios.get(`/api/v1/accounts/${accountId}/billing`);
      hasBillingPlan.value = !!res.data?.billing?.plan;
    }
  } catch {
    hasBillingPlan.value = false;
  }
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const recentKanbanItems = computed(() => {
  if (!selectedFunnel.value) return [];

  return (
    kanbanItems.value
      ?.slice()
      .sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;
      })
      .slice(0, 5) || []
  );
});

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
  {
    name: 'Reports Kanban',
    label: `${t('SIDEBAR.REPORTS')} ${store.getters['kanban/getKanbanTitle']}`,
    to: accountScopedRoute('kanban_reports'),
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  const currentUser = store.getters.getCurrentUser;
  const isSuperAdmin = currentUser?.type === 'SuperAdmin';
  const items = [
    // v6.4.19: HIDDEN - Opção "Caixa de Entrada" ocultada do menu lateral
    // Motivo: Decisão de UX para simplificar a navegação principal
    // Descomentar para reativar no futuro se necessário
    // {
    //   name: 'Inbox',
    //   label: t('SIDEBAR.INBOX'),
    //   icon: 'i-lucide-inbox',
    //   to: accountScopedRoute('inbox_view'),
    //   activeOn: ['inbox_view', 'inbox_view_conversation'],
    //   getterKeys: {
    //     count: 'notifications/getUnreadCount',
    //   },
    // },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      // v6.4.68: Pin essential items visible when section is collapsed
      pinnedItems: ['All', 'Unread'],
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        // v6.4.16: Reordenado para melhorar UX (Não lidas > Não atendidas > Menções)
        // Ordem anterior: Menções, Não atendidas, Não lidas
        {
          name: 'Unread',
          activeOn: ['conversation_through_unread'],
          label: t('SIDEBAR.UNREAD_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unread'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'Kanban',
      label: t('SIDEBAR.KANBAN_BOARD_TITLE', {
        title: store.getters['kanban/getKanbanTitle'],
      }),
      icon: 'i-lucide-layout-grid',
      featureFlag: 'kanban_board',
      children: [
        {
          name: 'Stages',
          label: 'Etapas',
          activeOn: ['kanban_board'],
          to: accountScopedRoute('kanban_board'),
        },
        // TODO: Removed - Calendar module will be independent (not a Kanban sub-item)
        // {
        //   name: 'Agenda',
        //   label: 'Agenda',
        //   activeOn: ['kanban_agenda'],
        //   to: accountScopedRoute('kanban_agenda'),
        // },
        ...(isStacklab
          ? [
              /*
              {
                name: 'Offers',
                label: 'Ofertas',
                icon: 'i-lucide-package-search',
                activeOn: ['kanban_offers'],
                to: accountScopedRoute(
                  'kanban_board',
                  {},
                  { view: 'offers' }
                ),
              },
              {
                name: 'Message Templates',
                label: 'Modelos de mensagens',
                icon: h('svg', {
                  xmlns: 'http://www.w3.org/2000/svg',
                  width: '16',
                  height: '16',
                  viewBox: '0 0 24 24',
                  fill: 'none',
                  stroke: 'currentColor',
                  'stroke-width': '2',
                  'stroke-linecap': 'round',
                  'stroke-linejoin': 'round',
                  class: 'lucide lucide-message-circle-more-icon lucide-message-circle-more'
                }, [
                  h('path', { d: 'M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719' }),
                  h('path', { d: 'M8 12h.01' }),
                  h('path', { d: 'M12 12h.01' }),
                  h('path', { d: 'M16 12h.01' })
                ]),
                activeOn: ['kanban_templates'],
                to: accountScopedRoute(
                  'kanban_board',
                  {},
                  { view: 'templates' }
                ),
              },
              */
            ]
          : []),
        {
          name: 'Funnels',
          label: 'Funis',
          icon: 'i-lucide-workflow',
          activeOn: ['kanban_board'],
          children:
            funnels.value?.map(funnel => ({
              name: `funnel-${funnel.id}`,
              label: funnel.name,
              to: accountScopedRoute('kanban_board', {}, { funnel: funnel.id }),
            })) || [],
        },
        // ...(selectedFunnel.value
        //   ? [
        //       {
        //         name: 'Recent Items',
        //         label: 'Itens Recentes',
        //         icon: 'i-lucide-clock',
        //         activeOn: ['kanban_board'],
        //         children:
        //           recentKanbanItems.value?.map(item => ({
        //             name: `item-${item.id}`,
        //             label: item.item_details?.title || `Item ${item.id}`,
        //             to: accountScopedRoute(
        //               'kanban_board',
        //               {},
        //               { item: item.id }
        //             ),
        //           })) || [],
        //       },
        //     ]
        //   : []),
      ],
    },
    {
      name: 'AIAssistant',
      icon: 'i-woot-captain',
      label: agentName.value,
      activeOn: ['captain_assistants_create_index'],
      children: [
        {
          name: 'Settings',
          label: t('SIDEBAR.CAPTAIN_SETTINGS'),
          activeOn: [
            'captain_assistants_settings_index',
            'captain_assistants_guidelines_index',
            'captain_assistants_guardrails_index',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_settings_index',
          }),
        },
        {
          name: 'Playground',
          label: t('SIDEBAR.CAPTAIN_PLAYGROUND'),
          activeOn: ['captain_assistants_playground_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_playground_index',
          }),
        },
        {
          name: 'FAQs',
          label: t('SIDEBAR.CAPTAIN_RESPONSES'),
          activeOn: [
            'captain_assistants_responses_index',
            'captain_assistants_responses_pending',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_responses_index',
          }),
        },
        {
          name: 'Documents',
          label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
          activeOn: ['captain_assistants_documents_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_documents_index',
          }),
        },
        // v6.4.67: Hidden 'Caixas de Entrada' (Inboxes) from Captain sidebar.
        // Uncomment to restore.
        // {
        //   name: 'Inboxes',
        //   label: t('SIDEBAR.CAPTAIN_INBOXES'),
        //   activeOn: ['captain_assistants_inboxes_index'],
        //   to: accountScopedRoute('captain_assistants_index', {
        //     navigationPath: 'captain_assistants_inboxes_index',
        //   }),
        // },
        {
          name: 'Scenarios',
          label: t('SIDEBAR.CAPTAIN_SCENARIOS'),
          activeOn: ['captain_assistants_scenarios_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_scenarios_index',
          }),
        },
        {
          name: 'Tools',
          label: t('SIDEBAR.CAPTAIN_TOOLS'),
          activeOn: ['captain_tools_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_tools_index',
          }),
        },
      ],
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        // HIDDEN: Active contacts option - commented for future use (Web Widget specific)
        // {
        //   name: 'Active',
        //   label: t('SIDEBAR.ACTIVE'),
        //   to: accountScopedRoute('contacts_dashboard_active'),
        //   activeOn: ['contacts_dashboard_active'],
        // },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        // v6.4.2: HIDDEN - Submenu "Marcado com" desabilitado temporariamente
        // Motivo: Usamos mais etiquetas de conversa do que de contato
        // Descomentar para reativar no futuro se necessário
        // {
        //   name: 'Tagged With',
        //   icon: 'i-lucide-tag',
        //   label: t('SIDEBAR.TAGGED_WITH'),
        //   children: labels.value.map(label => ({
        //     name: `${label.title}-${label.id}`,
        //     label: label.title,
        //     icon: h('span', {
        //       class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
        //       style: { backgroundColor: label.color },
        //     }),
        //     to: accountScopedRoute(
        //       'contacts_dashboard_labels_index',
        //       { label: label.title },
        //       { page: 1, search: undefined }
        //     ),
        //     activeOn: [
        //       'contacts_dashboard_labels_index',
        //       'contacts_edit_label',
        //     ],
        //   })),
        // },
      ],
    },
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: 'i-lucide-building-2',
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index'],
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        // {
        //   name: 'Reports Bot',
        //   label: t('SIDEBAR.REPORTS_BOT'),
        //   to: accountScopedRoute('bot_reports'),
        // },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Agent Assignment',
          label: t('SIDEBAR.AGENT_ASSIGNMENT'),
          icon: 'i-lucide-user-cog',
          to: accountScopedRoute('assignment_policy_index'),
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('automation_list'),
        },
        // {
        //   name: 'Settings Agent Bots',
        //   label: t('SIDEBAR.AGENT_BOTS'),
        //   icon: 'i-lucide-bot',
        //   to: accountScopedRoute('agent_bots'),
        // },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        ...(isSuperAdmin
          ? [
              {
                name: 'Settings Integrations',
                label: t('SIDEBAR.INTEGRATIONS'),
                icon: 'i-lucide-blocks',
                to: accountScopedRoute('settings_applications'),
              },
            ]
          : []),
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        ...(hasBillingPlan.value
          ? [
              {
                name: 'Settings Billing',
                label: t('SIDEBAR.BILLING'),
                icon: 'i-lucide-credit-card',
                to: accountScopedRoute('billing_settings_index'),
              },
            ]
          : []),
      ],
    },
    // NOTE: Commented out — "Novidades" is accessible via the profile popup menu.
    // Keeping sidebar clean to avoid stealing user attention.
    // {
    //   name: 'Changelog',
    //   label: 'Novidades',
    //   icon: 'i-lucide-sparkles',
    //   to: accountScopedRoute('changelog_index'),
    //   activeOn: ['changelog_index'],
    //   getterKeys: {
    //     count: 'changelogEntries/getChangelogUnreadCount',
    //   },
    // },
  ];

  // Add dashboard apps to the menu items
  const iconMap = {
    link: 'i-lucide-link',
    star: 'i-lucide-star',
    heart: 'i-lucide-heart',
    zap: 'i-lucide-zap',
    briefcase: 'i-lucide-briefcase',
    chart: 'i-lucide-bar-chart-2',
    phone: 'i-lucide-phone',
    mail: 'i-lucide-mail',
    message: 'i-lucide-message-circle',
    bell: 'i-lucide-bell',
    wrench: 'i-lucide-wrench',
    settings: 'i-lucide-settings',
    database: 'i-lucide-database',
    users: 'i-lucide-users',
    globe: 'i-lucide-globe',
    calendar: 'i-lucide-calendar',
    'file-text': 'i-lucide-file-text',
    'layout-dashboard': 'i-lucide-layout-dashboard',
    'shopping-bag': 'i-lucide-shopping-bag',
    flag: 'i-lucide-flag',
    map: 'i-lucide-map',
    video: 'i-lucide-video',
    shield: 'i-lucide-shield',
    rocket: 'i-lucide-rocket',
    book: 'i-lucide-book',
    cloud: 'i-lucide-cloud',
  };

  const dashboardAppItems = dashboardApps.value
    .filter(app => {
      // If only_admin is true, only show to admins and super admins
      if (app.only_admin) {
        return isSuperAdmin || store.getters.getCurrentRole === 'administrator';
      }
      return true;
    })
    .map(app => ({
      name: `app_${app.id}`,
      label: app.title,
      icon: iconMap[app.icon] || 'i-lucide-link',
      to: accountScopedRoute('dashboard_apps_show', { id: app.id }),
      toStateName: 'dashboard_apps_show',
    }));

  return [...items, ...dashboardAppItems];
});

// Auto-close mobile sidebar when navigating to a new page
const sidebarRoute = useRoute();

const closeMobileSidebar = () => {
  emit('closeMobileSidebar');
};

watch(
  () => sidebarRoute.fullPath,
  () => {
    if (props.isMobileSidebarOpen) {
      closeMobileSidebar();
    }
  }
);
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="rtl:border-l ltr:border-r border-n-weak flex flex-col text-sm pb-1 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 transition-[width,flex-basis,transform] duration-200 ease-in-out md:static md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:-translate-x-0"
    :class="[
      isCollapsed ? 'w-[52px] basis-[52px]' : 'w-[200px] basis-[200px]',
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'bg-n-solid-2': !isCustomStyleActive,
        'sidebar-custom-styled': isCustomStyleActive,
      },
    ]"
    :style="sidebarStyleVars"
  >
    <section class="grid gap-2 mt-2 mb-4">
      <div
        class="flex gap-2 items-center px-2 min-w-0"
        :class="{ 'justify-center': isCollapsed }"
      >
        <div
          v-if="!isCollapsed"
          class="grid flex-shrink-0 place-content-center size-6"
        >
          <Logo class="size-4" />
        </div>
        <template v-if="!isCollapsed">
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <SidebarAnnouncementBell />
      </div>
      <div v-if="!isCollapsed" class="flex gap-2 px-2">
        <RouterLink
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-11" />
          <span class="flex-grow text-left">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <ComposeConversation align-position="right" @close="onComposeClose">
          <template #trigger="{ toggle }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="!h-7 !bg-n-solid-3 dark:!bg-n-black/30 !outline-n-weak !text-n-slate-11"
              @click="onComposeOpen(toggle)"
            />
          </template>
        </ComposeConversation>
      </div>
      <!-- Collapsed mode: show search and compose as icons -->
      <div v-else class="flex flex-col gap-1 px-2 items-center">
        <RouterLink
          v-tooltip.right="t('COMBOBOX.SEARCH_PLACEHOLDER')"
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg hover:bg-n-alpha-2 text-n-slate-11"
        >
          <span class="i-lucide-search size-4" />
        </RouterLink>
      </div>
    </section>
    <nav class="grid overflow-y-scroll flex-grow gap-2 px-2 pb-5 no-scrollbar">
      <ul class="flex flex-col gap-1.5 m-0 list-none">
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="flex flex-col flex-shrink-0 relative gap-1 justify-between items-center"
    >
      <!-- REMOVED: gradient overlay + changelog card (not relevant for Konverte) -->
      <div
        class="p-1 flex-shrink-0 flex w-full justify-between z-10 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
      >
        <SidebarProfileMenu
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
        <SidebarSupportBell />
      </div>
    </section>
  </aside>

    <!-- Floating sidebar toggle (vertically centered on sidebar edge) -->
    <!-- v6.4.70: Hidden on mobile (md:) since MobileSidebarLauncher handles that -->
    <button
      v-tooltip.right="toggleSidebarTooltip"
      :aria-expanded="!isCollapsed"
      class="sidebar-edge-toggle hidden md:flex"
      :class="isCollapsed ? 'sidebar-edge-toggle--collapsed' : 'sidebar-edge-toggle--expanded'"
      @click="toggleSidebar"
    >
      <span
        class="size-3 transition-transform duration-200"
        :class="isCollapsed ? 'i-lucide-chevron-right' : 'i-lucide-chevron-left'"
      />
    </button>
</template>

<style>
/*
 * v6.4.66: Custom sidebar styling via CSS variables
 * These rules target elements inside .sidebar-custom-styled (the <aside>)
 * CSS variables are injected by useSidebarStyle composable
 */

/* Sidebar background */
.sidebar-custom-styled {
  background-color: var(--sidebar-bg) !important;
  border-color: var(--sidebar-bg) !important;
}

/* SidebarGroupHeader: default state (not active, not hovered) */
.sidebar-custom-styled [role='button']:not(.text-n-blue-text) {
  background-color: var(--sidebar-menu-btn-bg) !important;
  color: var(--sidebar-menu-btn-text) !important;
  border-radius: 8px !important;
}

/* SidebarGroupHeader: hover state */
.sidebar-custom-styled [role='button']:not(.text-n-blue-text):hover {
  background-color: var(--sidebar-hover-bg) !important;
}

/* SidebarGroupHeader: active state (has text-n-blue-text or bg-n-alpha-2) */
.sidebar-custom-styled [role='button'].text-n-blue-text,
.sidebar-custom-styled [role='button'].bg-n-alpha-2 {
  background-color: var(--sidebar-menu-btn-active-bg) !important;
  color: var(--sidebar-menu-btn-active-text) !important;
}

/* SidebarGroupLeaf: child items text */
.sidebar-custom-styled .child-item {
  color: var(--sidebar-submenu-text) !important;
}

/* SidebarGroupLeaf: active child item */
.sidebar-custom-styled .child-item .active {
  background-color: var(--sidebar-menu-btn-active-bg) !important;
  color: var(--sidebar-menu-btn-active-text) !important;
}

/* SidebarGroupLeaf: hover on child links */
.sidebar-custom-styled .child-item a:hover,
.sidebar-custom-styled .child-item div:hover {
  background-color: var(--sidebar-hover-bg) !important;
}

/* Icons inside sidebar */
.sidebar-custom-styled [role='button'] .size-4,
.sidebar-custom-styled .child-item .size-4 {
  color: var(--sidebar-icon-color) !important;
}

/* Active state icon color override */
.sidebar-custom-styled [role='button'].text-n-blue-text .size-4 {
  color: var(--sidebar-menu-btn-active-text) !important;
}

/* Profile/footer section text */
.sidebar-custom-styled section:last-child {
  color: var(--sidebar-menu-btn-text) !important;
}

/* Expand/collapse chevron icons */
.sidebar-custom-styled .i-lucide-chevron-up,
.sidebar-custom-styled .i-lucide-chevron-down {
  color: var(--sidebar-menu-btn-text) !important;
}

/* Badge border override for custom bg */
.sidebar-custom-styled .border-n-solid-2 {
  border-color: var(--sidebar-bg) !important;
}

/* Before pseudo-element (tree line connector) for child items */
.sidebar-custom-styled .child-item::before {
  background-color: var(--sidebar-submenu-text, currentColor) !important;
  opacity: 0.2;
}

.sidebar-custom-styled .child-item::after {
  border-color: var(--sidebar-submenu-text, currentColor) !important;
  opacity: 0.2;
}

/* ===== Floating sidebar edge toggle (v6.4.79) ===== */
/* Always visible, compact dark/light circle on sidebar edge */
/* Uses !important to prevent override by .sidebar-custom-styled and global resets */
.sidebar-edge-toggle {
  position: fixed !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  z-index: 41 !important;
  /* display is controlled by Tailwind hidden/md:flex */
  align-items: center !important;
  justify-content: center !important;
  width: 18px !important;
  height: 18px !important;
  min-width: 18px !important;
  min-height: 18px !important;
  max-width: 18px !important;
  max-height: 18px !important;
  border-radius: 50% !important;
  padding: 0 !important;
  /* Dark mode default: dark bg, light arrow */
  background: #1e293b !important;
  border: 1px solid rgba(71, 85, 105, 0.6) !important;
  color: #cbd5e1 !important;
  cursor: pointer !important;
  backdrop-filter: blur(6px);
  transition: all 0.2s ease, left 0.2s ease-in-out;
  opacity: 1 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  outline: none !important;
}

/* Force the icon span inside to inherit the color */
.sidebar-edge-toggle span,
.sidebar-edge-toggle > * {
  color: inherit !important;
  background-color: currentColor !important;
  width: 10px !important;
  height: 10px !important;
}

/* Dark mode hover: slightly lighter bg, blue arrow */
.sidebar-edge-toggle:hover {
  background: #334155 !important;
  color: #93c5fd !important;
  border-color: rgba(96, 165, 250, 0.5) !important;
  transform: translateY(-50%) scale(1.15) !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
}

.sidebar-edge-toggle--expanded {
  left: 191px !important; /* 200px sidebar - 9px (half of 18px) */
}

.sidebar-edge-toggle--collapsed {
  left: 43px !important; /* 52px sidebar - 9px */
}

/* Light mode: ALSO dark bg with light arrow (consistent across themes) */
:root:not(.dark) .sidebar-edge-toggle {
  background: #334155 !important;
  color: #e2e8f0 !important;
  border-color: rgba(71, 85, 105, 0.5) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}

:root:not(.dark) .sidebar-edge-toggle span,
:root:not(.dark) .sidebar-edge-toggle > * {
  color: inherit !important;
  background-color: currentColor !important;
}

/* Light mode hover: darker bg, blue arrow */
:root:not(.dark) .sidebar-edge-toggle:hover {
  background: #1e293b !important;
  color: #93c5fd !important;
  border-color: rgba(96, 165, 250, 0.4) !important;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}
</style>
